import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { validateEnvironment, formatValidationResults, getSafeEnvironmentVariables } from './utils/validation';
import config from './config/index';
import { FEATURE_FLAGS } from './config/featureFlags';
import { initNativeUI } from './utils/native-ui';
import { initSentry } from './sentry';
import { initPostHog } from './posthog';

// Expose build metadata globally for debugging
declare global {
  const __BUILD_TIME__: string;
  const __BUILD_VERSION__: string;
  interface Window {
    __BUILD_VERSION__: string;
    __BUILD_TIME__: string;
  }
}

window.__BUILD_VERSION__ = __BUILD_VERSION__;
window.__BUILD_TIME__ = __BUILD_TIME__;

// KAN-374: stamp the deployed git SHA into the document so production UAT
// can verify a deploy actually advanced past `git push origin main`. We
// prefer the Vercel-injected commit SHA over the build-version timestamp;
// edge functions surface the same `release` tag, so frontend + backend
// events join under one identifier in Sentry.
const __FRONTEND_RELEASE__ =
  (import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA as string | undefined) ||
  __BUILD_VERSION__;
try {
  if (typeof document !== 'undefined') {
    const head = document.head;
    let metaRelease = head.querySelector('meta[name="release"]') as
      | HTMLMetaElement
      | null;
    if (!metaRelease) {
      metaRelease = document.createElement('meta');
      metaRelease.setAttribute('name', 'release');
      head.appendChild(metaRelease);
    }
    metaRelease.setAttribute('content', __FRONTEND_RELEASE__);
  }
} catch {
  /* SSR / no-document — ignore */
}

// Initialize Sentry as early as possible so init-phase errors get captured.
// No-ops gracefully when VITE_SENTRY_DSN is unset.
// See aightbet/frontend/src/sentry.ts + Architecture Reality Check §7.
initSentry();

// Initialize PostHog. No-ops gracefully when VITE_POSTHOG_KEY is unset.
// User identification happens later from the SupabaseAuthContext after
// the session establishes. See aightbet/frontend/src/posthog.ts.
initPostHog();

// Initialize native UI (status bar, keyboard) — no-ops on web
initNativeUI();

console.log(`[AightBet] Build Version: ${__BUILD_VERSION__}`);
console.log(`[AightBet] Build Time: ${__BUILD_TIME__}`);

// Validate environment variables before app initialization
const validationResult = validateEnvironment();

// Log validation results in development
if (FEATURE_FLAGS.DEBUG_MODE_ENABLED) {
  console.log('[Environment] Configuration:', getSafeEnvironmentVariables());
  if (validationResult.warnings.length > 0 || !validationResult.isValid) {
    console.log('[Environment] Validation results:', formatValidationResults(validationResult));
  }
}

// Show error page if critical environment variables are missing
if (!validationResult.isValid && config.environment.isProduction) {
  const errorContainer = document.getElementById('root');
  if (errorContainer) {
    errorContainer.innerHTML = `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100vh;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        background-color: #f5f5f5;
        padding: 20px;
      ">
        <h1 style="color: #d32f2f; margin-bottom: 20px;">Configuration Error</h1>
        <div style="
          background: white;
          border-radius: 8px;
          padding: 20px;
          max-width: 600px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        ">
          <p style="margin-bottom: 15px;">The application cannot start due to missing configuration.</p>
          <pre style="
            background: #f5f5f5;
            padding: 15px;
            border-radius: 4px;
            overflow-x: auto;
            font-size: 14px;
            white-space: pre-wrap;
          ">${formatValidationResults(validationResult).replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
          <p style="margin-top: 15px; color: #666;">
            Please contact support if this issue persists.
          </p>
        </div>
      </div>
    `;
  }
} else {
  // Initialize React app
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );

  // Log environment info
  if (config.environment.isDevelopment) {
    console.log(`[AightBet] Running in ${config.environment.name} mode`);
    console.log(`[AightBet] API: ${config.api.baseUrl || 'Using proxy'}`);
    console.log(`[AightBet] WebSocket: ${config.api.wsUrl}`);
    console.log(`[AightBet] Version: ${config.environment.version}`);
  }
}